import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  FaBirthdayCake,
  FaLayerGroup,
  FaQuestionCircle,
  FaWeight,
} from "react-icons/fa";

import calculateAge from "../../../utils/calculateAge";
import getCategory from "../../../utils/getCategory";
import getWeightCategory from "../../../utils/getWeightCategory";

const isValidValue = (value) =>
  value !== null && value !== undefined && String(value).trim() !== "";

const CategoryMetric = ({ icon: Icon, label, value, helper, variant = "sky" }) => {
  const variants = {
    sky: {
      icon: "bg-sky-100 text-sky-700 dark:bg-sky-500/10 dark:text-sky-200",
      chip: "bg-sky-50 text-sky-800 dark:bg-sky-500/10 dark:text-sky-100",
    },
    orange: {
      icon: "bg-orange-100 text-orange-700 dark:bg-orange-500/10 dark:text-orange-200",
      chip: "bg-orange-50 text-orange-800 dark:bg-orange-500/10 dark:text-orange-100",
    },
    emerald: {
      icon: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-200",
      chip: "bg-emerald-50 text-emerald-800 dark:bg-emerald-500/10 dark:text-emerald-100",
    },
  };

  const style = variants[variant] || variants.sky;

  return (
    <div className="rounded-[1.35rem] border border-slate-200 bg-white p-4 shadow-sm dark:border-white/10 dark:bg-white/[0.05]">
      <div className="flex items-start gap-3">
        <div
          className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl ${style.icon}`}
        >
          <Icon />
        </div>

        <div className="min-w-0 flex-1">
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">
            {label}
          </p>

          <p
            className={`mt-2 inline-flex max-w-full rounded-full px-3 py-1 text-sm font-bold ${style.chip}`}
          >
            <span className="truncate">{value}</span>
          </p>

          {helper && (
            <p className="mt-2 text-xs font-medium leading-5 text-slate-500 dark:text-white/45">
              {helper}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

const CategorieJudoka = ({ genre, date_naissance, poids }) => {
  const { t } = useTranslation();

  const categoryData = useMemo(() => {
    if (!date_naissance) {
      return {
        age: null,
        ageCategory: t("judokaCategory.notDefined", {
          defaultValue: "Non défini",
        }),
        weightCategory: t("judokaCategory.notDefined", {
          defaultValue: "Non défini",
        }),
        isComplete: false,
        missingReason: "La date de naissance est nécessaire pour calculer la catégorie.",
      };
    }

    const calculatedAge = calculateAge(date_naissance);
    const calculatedAgeCategory = getCategory(date_naissance);

    const canCalculateWeight =
      isValidValue(genre) &&
      isValidValue(poids) &&
      isValidValue(calculatedAgeCategory);

    const calculatedWeightCategory = canCalculateWeight
      ? getWeightCategory(genre, calculatedAgeCategory, poids)
      : t("judokaCategory.notDefined", {
          defaultValue: "Non défini",
        });

    let missingReason = "";

    if (!isValidValue(genre)) {
      missingReason = "Le genre est nécessaire pour déterminer la catégorie de poids.";
    } else if (!isValidValue(poids)) {
      missingReason = "Le poids est nécessaire pour déterminer la catégorie de poids.";
    }

    return {
      age: calculatedAge,
      ageCategory:
        calculatedAgeCategory ||
        t("judokaCategory.notDefined", {
          defaultValue: "Non défini",
        }),
      weightCategory: calculatedWeightCategory,
      isComplete: canCalculateWeight,
      missingReason,
    };
  }, [date_naissance, genre, poids, t]);

  const ageLabel =
    categoryData.age !== null
      ? `${categoryData.age} ${t("judokaCategory.years", {
          defaultValue: "ans",
        })}`
      : t("judokaCategory.notDefined", {
          defaultValue: "Non défini",
        });

  return (
    <div className="w-full">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-orange-600 dark:text-orange-300">
            Catégorisation
          </p>

          <h3 className="mt-1 font-titre text-2xl font-bold leading-tight text-slate-950 dark:text-white">
            {t("judokaCategory.title", {
              defaultValue: "Catégorie du judoka",
            })}
          </h3>
        </div>

        <span
          className={`inline-flex items-center gap-2 rounded-full px-3 py-2 text-xs font-bold ${
            categoryData.isComplete
              ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-200"
              : "bg-slate-100 text-slate-500 dark:bg-white/10 dark:text-white/50"
          }`}
        >
          {categoryData.isComplete ? "Calcul complet" : "À compléter"}
        </span>
      </div>

      {categoryData.missingReason && (
        <div className="mb-4 rounded-[1.2rem] border border-orange-200 bg-orange-50 p-3 text-sm font-semibold leading-6 text-orange-800 dark:border-orange-400/20 dark:bg-orange-500/10 dark:text-orange-100">
          <div className="flex items-start gap-2">
            <FaQuestionCircle className="mt-1 shrink-0" />
            <span>{categoryData.missingReason}</span>
          </div>
        </div>
      )}

      <div className="grid gap-3">
        <CategoryMetric
          icon={FaBirthdayCake}
          label={t("judokaCategory.age", {
            defaultValue: "Âge",
          })}
          value={ageLabel}
          helper="Calculé à partir de la date de naissance."
          variant="sky"
        />

        <CategoryMetric
          icon={FaLayerGroup}
          label={t("judokaCategory.ageCategory", {
            defaultValue: "Catégorie d’âge",
          })}
          value={categoryData.ageCategory}
          helper="Catégorie sportive selon l’année de naissance."
          variant="orange"
        />

        <CategoryMetric
          icon={FaWeight}
          label={t("judokaCategory.weightCategory", {
            defaultValue: "Catégorie de poids",
          })}
          value={categoryData.weightCategory}
          helper="La catégorie de poids dépend du genre, de l’âge et du poids."
          variant="emerald"
        />
      </div>
    </div>
  );
};

export default CategorieJudoka;